/* AdminComplianceFindingsPage — FASE 8.5 (v1.14.78.0)
 * Dashboard di tracking dei 33 findings dell'audit legal/security 2026.
 * Endpoint usati:
 *   - GET  /api/admin/compliance/findings/stats
 *   - GET  /api/admin/compliance/findings?severity=&status=&category=
 *   - POST /api/admin/compliance/findings/{id}/status
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ShieldCheck, AlertTriangle, CircleDot, CheckCircle2, XCircle, RefreshCcw,
  Filter, Edit3, Save, X as XIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosCfg = { withCredentials: true };

function authHeaders() {
  try {
    const tok = JSON.parse(localStorage.getItem("colucci_admin_session") || "{}").token || "";
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  } catch { return {}; }
}

const SEVERITY_CONFIG = {
  critical: { label: "Critico", color: "bg-red-600 text-white", border: "border-red-300" },
  high:     { label: "Alto",    color: "bg-orange-500 text-white", border: "border-orange-300" },
  medium:   { label: "Medio",   color: "bg-amber-500 text-white", border: "border-amber-300" },
  low:      { label: "Basso",   color: "bg-zinc-400 text-white", border: "border-zinc-300" },
};

const STATUS_CONFIG = {
  open:           { label: "Aperto",         icon: CircleDot, color: "text-zinc-600 bg-zinc-100" },
  in_progress:    { label: "In Corso",       icon: RefreshCcw, color: "text-blue-700 bg-blue-100" },
  resolved:       { label: "Risolto",        icon: CheckCircle2, color: "text-emerald-700 bg-emerald-100" },
  wontfix:        { label: "Non Risolto",    icon: XCircle, color: "text-red-700 bg-red-100" },
  accepted_risk:  { label: "Rischio Accettato", icon: AlertTriangle, color: "text-amber-700 bg-amber-100" },
};

export default function AdminComplianceFindingsPage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSev, setFilterSev] = useState("");
  const [filterStat, setFilterStat] = useState("");
  const [editing, setEditing] = useState(null); // finding_id being edited

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterSev) q.set("severity", filterSev);
      if (filterStat) q.set("status", filterStat);
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/compliance/findings/stats`, { headers: authHeaders(), ...axiosCfg }),
        axios.get(`${API}/admin/compliance/findings?${q.toString()}`, { headers: authHeaders(), ...axiosCfg }),
      ]);
      setStats(s.data);
      setItems(l.data?.items || []);
    } catch (err) {
      toast.error("Errore caricamento: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterSev, filterStat]);

  useEffect(() => { reload(); }, [reload]);

  const updateStatus = async (fid, newStatus, notes) => {
    try {
      await axios.post(`${API}/admin/compliance/findings/${fid}/status`,
        { status: newStatus, notes: notes || null },
        { headers: authHeaders(), ...axiosCfg });
      toast.success(`Finding ${fid}: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const scoreColor = useMemo(() => {
    const s = stats?.compliance_score_pct || 0;
    if (s >= 90) return "text-emerald-700";
    if (s >= 70) return "text-amber-700";
    if (s >= 40) return "text-orange-700";
    return "text-red-700";
  }, [stats]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-compliance-findings-page">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">Sistema · Compliance</p>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#163321] flex items-center gap-3">
              <ShieldCheck className="text-[#163321]" size={32} /> Compliance Score
            </h1>
            <p className="text-sm text-[#4A5E51] mt-2 max-w-2xl">
              Tracking dei 33 findings emessi dall&apos;Audit Legal/Security/Compliance (FASE 8 — feb 2026).
              Aggiorna lo stato di ciascuno man mano che la remediation procede.
            </p>
          </div>
          <Button onClick={reload} variant="outline" data-testid="compliance-reload-btn">
            <RefreshCcw size={16} className="mr-2" /> Aggiorna
          </Button>
        </div>
      </header>

      {/* Score + KPI */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 col-span-1 sm:col-span-2" data-testid="kpi-score">
            <p className="text-xs uppercase tracking-wider text-[#4A5E51] mb-1">Compliance Score</p>
            <div className="flex items-end gap-3">
              <span className={`text-5xl font-bold ${scoreColor}`}>{stats.compliance_score_pct}%</span>
              <span className="text-sm text-[#4A5E51] mb-2">{stats.handled}/{stats.total} gestiti</span>
            </div>
            <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden mt-3">
              <div className={`h-full rounded-full transition-all ${
                stats.compliance_score_pct >= 90 ? "bg-emerald-500" :
                stats.compliance_score_pct >= 70 ? "bg-amber-500" :
                stats.compliance_score_pct >= 40 ? "bg-orange-500" : "bg-red-500"
              }`} style={{ width: `${stats.compliance_score_pct}%` }} />
            </div>
          </div>

          {["critical", "high", "medium"].map((sev) => {
            const cfg = SEVERITY_CONFIG[sev];
            const total = Object.values(stats.by_severity_status[sev] || {}).reduce((a,b)=>a+b, 0);
            const resolved = (stats.by_severity_status[sev]?.resolved || 0);
            return (
              <div key={sev} className={`bg-white rounded-2xl border ${cfg.border} p-4`} data-testid={`kpi-sev-${sev}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                </div>
                <div className="text-2xl font-bold text-[#163321]">{resolved}/{total}</div>
                <p className="text-xs text-[#4A5E51]">risolti</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter size={16} className="text-[#4A5E51]" />
          <select value={filterSev} onChange={(e) => setFilterSev(e.target.value)} className="px-3 py-1.5 border border-[#E5E2DB] rounded-lg text-sm" data-testid="filter-severity">
            <option value="">Tutte le severity</option>
            <option value="critical">Critico</option>
            <option value="high">Alto</option>
            <option value="medium">Medio</option>
            <option value="low">Basso</option>
          </select>
          <select value={filterStat} onChange={(e) => setFilterStat(e.target.value)} className="px-3 py-1.5 border border-[#E5E2DB] rounded-lg text-sm" data-testid="filter-status">
            <option value="">Tutti gli stati</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <span className="text-xs text-[#4A5E51]">{items.length} risultati</span>
        </div>
      </div>

      {loading && <p className="text-sm text-[#4A5E51]">Caricamento…</p>}

      {/* Findings list */}
      <div className="space-y-2" data-testid="findings-list">
        {items.map((f) => {
          const sevCfg = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.low;
          const stCfg = STATUS_CONFIG[f.status] || STATUS_CONFIG.open;
          const StIcon = stCfg.icon;
          const isEditing = editing === f.finding_id;
          return (
            <div key={f.finding_id} className={`bg-white rounded-xl border ${sevCfg.border} p-4`} data-testid={`finding-${f.finding_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <code className="text-xs font-mono text-[#4A5E51] bg-[#FCFCFA] px-1.5 py-0.5 rounded">{f.finding_id}</code>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${sevCfg.color}`}>{sevCfg.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium flex items-center gap-1 ${stCfg.color}`}>
                      <StIcon size={12} /> {stCfg.label}
                    </span>
                    {f.phase_remediated && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        FASE {f.phase_remediated} applicata
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-[#163321]">{f.title}</p>
                  <p className="text-xs text-[#4A5E51] mt-1">
                    <strong>Sezione:</strong> {f.section} · <strong>Categoria:</strong> {f.category}
                  </p>
                  <p className="text-xs text-[#4A5E51] mt-1.5"><strong>Remediation:</strong> {f.remediation}</p>
                  {f.notes && <p className="text-xs italic text-[#4A5E51] mt-1.5 border-l-2 border-[#D9C49A] pl-2">{f.notes}</p>}
                  {f.resolved_by && (
                    <p className="text-[10px] text-emerald-700 mt-1">
                      Risolto da {f.resolved_by} il {(f.resolved_at || "").slice(0,10)}
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  {!isEditing ? (
                    <Button variant="outline" size="sm" onClick={() => setEditing(f.finding_id)} data-testid={`edit-${f.finding_id}`}>
                      <Edit3 size={14} className="mr-1" /> Modifica
                    </Button>
                  ) : (
                    <EditForm finding={f} onSave={(st, nt) => updateStatus(f.finding_id, st, nt)} onCancel={() => setEditing(null)} />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <p className="text-sm text-[#4A5E51] text-center py-8">Nessun finding corrispondente ai filtri.</p>
        )}
      </div>
    </div>
  );
}

function EditForm({ finding, onSave, onCancel }) {
  const [status, setStatus] = useState(finding.status);
  const [notes, setNotes] = useState(finding.notes || "");
  return (
    <div className="bg-[#FCFCFA] rounded-lg p-3 border border-[#E5E2DB] flex flex-col gap-2 min-w-[260px]" data-testid={`edit-form-${finding.finding_id}`}>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-[#E5E2DB] rounded text-sm">
        {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opzionale)"
        rows={2} className="px-2 py-1.5 border border-[#E5E2DB] rounded text-xs" maxLength={1000} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(status, notes)} className="flex-1 bg-[#163321]" data-testid={`save-${finding.finding_id}`}>
          <Save size={14} className="mr-1" /> Salva
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid={`cancel-${finding.finding_id}`}>
          <XIcon size={14} />
        </Button>
      </div>
    </div>
  );
}
